import { queryClient } from '@/router'
import { type FC, useCallback } from 'react'

import { TableRow } from '@/components/table/table'
import { Tag } from '@/components/tag/tag'

import { useDialog } from '@/lib/hooks/use-dialog'
import { getApplicantDetailQuery } from '@/lib/queries/business/v1/applicant/queries'
import type { ResApplicant } from '@/lib/queries/business/v2/applicant/search/types'
import { cn } from '@/lib/utils'

import { DeleteApplicantDialog } from '../../../applicant-dialogs/delete-applicant-dialog/delete-applicant-dialog'
import { EditApplicantDialog } from '../../../applicant-dialogs/edit-applicant-dialog/edit-applicant-dialog'
import { SelectButton } from '../select-button/select-button'
import { ApplicantsTableCell } from './ui/applicants-table-cell/applicants-table-cell'
import { CellContentActions } from './ui/cell-content-actions.tsx/cell-content-actions'

interface Props {
  selected: boolean
  applicant: ResApplicant
  invalidate: () => Promise<void>
  onSelectionToggle: (applicantId: number) => void
}

export const ApplicantRow: FC<Props> = ({ selected, applicant, invalidate, onSelectionToggle }) => {
  const {
    isDialogOpen: isEditApplicantDialogOpen,
    openDialog: openEditApplicantDialog,
    closeDialog: closeEditApplicantDialog,
  } = useDialog()

  const { isDialogOpen: isDeleteDialogOpen, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog()

  const handleEdited = useCallback(async () => {
    await Promise.all([
      invalidate(),
      // We also need to invalidate the detail query because the Edit dialog
      // uses this query to populate the form fields. If we don't do this and
      // open the Edit dialog again, the form fields will not be populated with
      // the latest data.
      queryClient.invalidateQueries({
        queryKey: getApplicantDetailQuery(String(applicant.path_id)).queryKey,
      }),
    ])
  }, [applicant.path_id, invalidate])

  return (
    <>
      <TableRow
        key={applicant.applicant_id}
        className={cn(
          'hover:bg-transparent hover:outline hover:outline-1 hover:outline-offset-[-1px] hover:outline-black [&>td]:py-0'
        )}
      >
        <ApplicantsTableCell noPadding id={applicant.path_id}>
          <SelectButton disabled={false} checked={selected} onClick={() => onSelectionToggle(applicant.path_id)} />
        </ApplicantsTableCell>

        <ApplicantsTableCell id={applicant.path_id} className={cn(applicant.isUpdated ? 'font-bold' : 'font-medium')}>
          <div className="flex items-center gap-2">
            {applicant.isUpdated && <NewUpdateMark />}
            {applicant.applicant_name}
          </div>
        </ApplicantsTableCell>

        <ApplicantsTableCell className={cn(applicant.isUpdated ? 'font-bold' : 'font-medium')} id={applicant.path_id}>
          {applicant.email || '-'}
        </ApplicantsTableCell>

        <ApplicantsTableCell className={cn(applicant.isUpdated ? 'font-bold' : 'font-medium')} id={applicant.path_id}>
          {applicant.phone || '-'}
        </ApplicantsTableCell>

        <ApplicantsTableCell id={applicant.path_id}>
          <div className="flex flex-wrap items-center gap-[7px]">
            {applicant.tags?.map((tag) => <Tag key={tag}>{tag}</Tag>)}
            {(!applicant.tags || applicant.tags.length === 0) && '-'}
          </div>
        </ApplicantsTableCell>

        <ApplicantsTableCell id={applicant.path_id}>
          <CellContentActions onEdit={openEditApplicantDialog} onDelete={openDeleteDialog} />
        </ApplicantsTableCell>
      </TableRow>

      <EditApplicantDialog
        open={isEditApplicantDialogOpen}
        pathId={String(applicant.path_id)}
        onEdited={handleEdited}
        onClose={closeEditApplicantDialog}
      />

      <DeleteApplicantDialog
        applicant={applicant}
        open={isDeleteDialogOpen}
        onDeleted={invalidate}
        onClose={closeDeleteDialog}
      />
    </>
  )
}

const NewUpdateMark = () => {
  return (
    <div
      className={cn(
        'h-[20px] w-[29px] rounded-md border border-blue-100 bg-blue-50',
        'flex items-center justify-center',
        'text-xs font-bold text-blue-500'
      )}
    >
      N
    </div>
  )
}
